import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { STATUSES, WEATHERS, todayISO } from "../lib/api";

const empty = () => ({ date: todayISO(), team_id: "", job_id: "", status: "in_progress", progress: 0, notes: "", weather: "Sunny" });

const Pick = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1.5">
    <Label className="label">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const LogDialog = ({ open, onOpenChange, initial, teams, jobs, onSave }) => {
  const [f, setF] = useState(empty());
  useEffect(() => { if (open) setF(initial ? { ...initial } : empty()); }, [open, initial]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const valid = f.date && f.team_id && f.job_id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="daily-log-modal" className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">{initial ? "Edit Daily Log" : "New Daily Log"}</DialogTitle><DialogDescription>Record the day's work for a team and job.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1.5">
            <Label className="label">Date (day / month / year)</Label>
            <Input data-testid="daily-log-modal-date-picker" type="date" value={f.date} onChange={(e) => set("date")(e.target.value)} />
          </div>
          <Pick label="Team" value={f.team_id} onChange={set("team_id")} testId="daily-log-modal-team-select" options={teams.map((t) => ({ value: t.id, label: t.name }))} />
          <Pick label="Job" value={f.job_id} onChange={set("job_id")} testId="daily-log-modal-job-select" options={jobs.map((j) => ({ value: j.id, label: `${j.no}. ${j.name}` }))} />
          <Pick label="Status" value={f.status} onChange={(v) => setF((p) => ({ ...p, status: v, progress: v === "done" ? 100 : p.progress }))} testId="daily-log-modal-status-select" options={STATUSES} />
          <Pick label="Weather" value={f.weather} onChange={set("weather")} testId="daily-log-modal-weather-select" options={WEATHERS.map((w) => ({ value: w, label: w }))} />
          <div className="col-span-2 space-y-1.5">
            <Label className="label">Progress: <span className="text-amber-400">{f.progress}%</span></Label>
            <Input data-testid="daily-log-modal-progress-input" type="number" min={0} max={100} value={f.progress}
              onChange={(e) => set("progress")(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="label">Notes</Label>
            <Textarea data-testid="daily-log-modal-notes-input" rows={3} value={f.notes} onChange={(e) => set("notes")(e.target.value)} placeholder="Work performed, findings, constraints..." />
          </div>
        </div>
        <p className="text-xs text-[color:var(--muted)]">Saving a log also updates this team's status for the selected job.</p>
        <DialogFooter>
          <Button variant="ghost" data-testid="daily-log-modal-cancel-button" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="daily-log-modal-save-button" disabled={!valid} onClick={() => onSave(f)} className="bg-amber-500 text-black hover:bg-amber-400">Save Log</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
